import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router';
import { CheckCircle2, AlertCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Button } from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { supabase } from '@/shared/lib/supabase';
import { fireAdsPurchase } from '@/shared/lib/ads';
import { PaymentTrustStrip } from '@/shared/components/feedback/PaymentTrustStrip';

/**
 * PayInvoicePage — public, no-auth route at /pay/:id?token=…
 *
 * Lands the customer here from the "Pay R… by card" button in the
 * invoice_issued email. Calls payfast-public-initiate with the
 * { invoice_id, payment_token } combination, receives a signed PayFast
 * form, mounts it in a hidden form element and auto-submits → browser
 * navigates to PayFast hosted checkout.
 *
 * On return from PayFast:
 *   /pay/:id?paid=1       → success card + link to portal
 *   /pay/:id?cancelled=1  → "you cancelled" card + retry button
 *
 * Unlike the in-portal flow (InvoiceDetailPage), this page does NOT show
 * invoice details — those live in the email itself per the user's UX
 * direction. The page is essentially a redirect with a friendly fallback.
 *
 * Spec: Phase 9 PayFast email click-through flow.
 */

interface PayFastInitiateResponse {
  ok: true;
  action_url: string;
  fields: Record<string, string>;
}

type Phase = 'redirecting' | 'paid' | 'cancelled' | 'error';

export default function PayInvoicePage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const paid = searchParams.get('paid');
  const cancelled = searchParams.get('cancelled');
  // Auto-pay migration flow (2026-07-05): &autopay=1 routes through
  // hosting-subscription-public-initiate — same invoice settlement, but the
  // PayFast checkout is a subscription, so the card is saved and future
  // months charge automatically.
  const autopay = searchParams.get('autopay') === '1';

  const [phase, setPhase] = useState<Phase>(() =>
    paid === '1' ? 'paid' : cancelled === '1' ? 'cancelled' : 'redirecting'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    // Return-URL paths are terminal — don't try to redirect again.
    if (phase !== 'redirecting') return;

    // Token + invoice id are required to initiate.
    if (!id || !token) {
      setErrorMessage(
        "This payment link is missing some details. Please use the link from your invoice email, or sign in to the portal to pay."
      );
      setPhase('error');
      return;
    }

    let cancelledBeforeRedirect = false;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke<PayFastInitiateResponse>(
          autopay ? 'hosting-subscription-public-initiate' : 'payfast-public-initiate',
          {
            body: { invoice_id: id, payment_token: token },
          }
        );

        if (cancelledBeforeRedirect) return;

        if (error) {
          let code = 'unknown';
          try {
            const ctx = (error as { context?: { response?: Response } }).context;
            if (ctx?.response) {
              const body = await ctx.response.clone().json();
              if (typeof body?.code === 'string') code = body.code;
            }
          } catch {
            /* ignore */
          }
          setErrorMessage(friendlyErrorMessage(code));
          setPhase('error');
          return;
        }

        if (!data?.ok) {
          setErrorMessage(friendlyErrorMessage('unknown'));
          setPhase('error');
          return;
        }

        // Build hidden form, submit, navigate away. Vanilla DOM.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.action_url;
        form.style.display = 'none';
        Object.entries(data.fields).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = String(v);
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        // Browser navigates here.
      } catch (err) {
        if (cancelledBeforeRedirect) return;
        setErrorMessage((err as Error).message || friendlyErrorMessage('unknown'));
        setPhase('error');
      }
    })();

    return () => {
      cancelledBeforeRedirect = true;
    };
  }, [id, token, phase, autopay]);

  // Fire the Google Ads purchase conversion once payment is CONFIRMED. The
  // ?paid=1 browser return usually beats the PayFast ITN that flips
  // lifecycle_state → 'paid', so we poll public_invoice_conversion_facts up to
  // 6×/2s and fire on the first 'paid'. If it never settles in-window we skip
  // (conservative under-count, never over-count). fireAdsPurchase itself gates
  // on marketing consent + dedupes. See
  // _bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md (AD-1/AD-3).
  useEffect(() => {
    if (phase !== 'paid' || !id || !token) return;
    const invoiceId = id;
    const paymentToken = token;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const MAX_ATTEMPTS = 6;

    async function poll() {
      if (cancelled) return;
      attempts += 1;
      const { data, error } = await supabase.rpc('public_invoice_conversion_facts', {
        p_invoice_id: invoiceId,
        p_payment_token: paymentToken,
      });
      if (cancelled) return;
      const row = (Array.isArray(data) ? data[0] : data) as
        | { status?: string; total?: number | string; number?: string }
        | null
        | undefined;
      if (!error && row && row.status === 'paid') {
        fireAdsPurchase({
          invoiceId,
          value: Number(row.total),
          number: String(row.number),
        });
        return;
      }
      if (attempts < MAX_ATTEMPTS) timer = setTimeout(poll, 2000);
    }

    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase, id, token]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-3">
          <img
            src="/images/logo.png"
            alt=""
            aria-hidden
            width={36}
            height={36}
            className="h-9 w-auto"
          />
          <span className="text-lg font-semibold text-navy-primary">
            {siteConfig.name}
          </span>
        </div>

        <div className="rounded-lg border border-border bg-card p-8 shadow-sm">
          {phase === 'redirecting' && <RedirectingState autopay={autopay} />}
          {phase === 'paid' && <PaidState invoiceId={id} token={token} autopay={autopay} />}
          {phase === 'cancelled' && (
            <CancelledState invoiceId={id} token={token} autopay={autopay} />
          )}
          {phase === 'error' && <ErrorState message={errorMessage} />}
        </div>

        <PaymentTrustStrip className="mt-6" />

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Need help? Email{' '}
          <a
            href={`mailto:${siteConfig.supportEmail}`}
            className="text-primary hover:underline"
          >
            {siteConfig.supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
}

function RedirectingState({ autopay }: { autopay?: boolean }) {
  return (
    <div className="text-center">
      <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-navy-primary">
        Redirecting to PayFast…
      </h1>
      <p className="mt-2 text-sm text-foreground-soft">
        You'll be sent to PayFast's secure checkout in a moment. Please don't
        close this window.
        {autopay &&
          ' Your card will be saved securely by PayFast so future months are billed automatically.'}
      </p>
    </div>
  );
}

function PaidState({
  invoiceId,
  token,
  autopay,
}: {
  invoiceId: string | undefined;
  token: string | null;
  autopay?: boolean;
}) {
  type SubPhase = 'idle' | 'starting' | 'no_plan' | 'error';
  const [subPhase, setSubPhase] = useState<SubPhase>('idle');
  const [subError, setSubError] = useState<string>('');

  // Studio Starter installment-plan handoff. After deposit clears, customer
  // returns here. If they chose an installment plan, the deposit is paid
  // but the recurring subscription must still be registered. This button
  // calls payfast-subscription-initiate which authenticates via the same
  // payment_token + service_order_id and auto-submits a PayFast subscription
  // form. If the order has no plan_months (full upfront), the EF returns
  // 'no_installment_plan' and we surface a friendly "you're all set" state.
  async function handleSetupPlan() {
    if (!invoiceId || !token) return;
    setSubPhase('starting');
    setSubError('');
    try {
      // First we need the service_order_id linked to this invoice. The
      // subscription-initiate EF takes service_order_id, not invoice_id.
      // We resolve it by reading the invoice via supabase REST (RLS allows
      // public-token access via the existing payfast-public-initiate path
      // pattern — but for the subscription-initiate we need a slim helper).
      //
      // Simplest: call payfast-subscription-initiate with the service_order
      // resolved server-side. We extend that EF to ALSO accept invoice_id
      // as an alternative input shape (TODO if not yet supported).
      //
      // For Slice 10.3 we instead resolve service_order_id client-side by
      // calling a small public RPC.
      const supaUrl = (import.meta as { env: Record<string, string> }).env.VITE_SUPABASE_URL ?? '';
      const supaAnon = (import.meta as { env: Record<string, string> }).env.VITE_SUPABASE_ANON_KEY ?? '';
      const rpcRes = await fetch(`${supaUrl}/rest/v1/rpc/service_order_id_for_invoice_token`, {
        method: 'POST',
        headers: {
          apikey: supaAnon,
          Authorization: `Bearer ${supaAnon}`,
          'Content-Type': 'application/json',
        } as Record<string, string>,
        body: JSON.stringify({ p_invoice_id: invoiceId, p_payment_token: token }),
      });
      if (!rpcRes.ok) {
        setSubError('Could not look up your order. Please email support.');
        setSubPhase('error');
        return;
      }
      const serviceOrderId = await rpcRes.json();
      if (!serviceOrderId || typeof serviceOrderId !== 'string') {
        setSubError('Could not link this payment to a service order.');
        setSubPhase('error');
        return;
      }

      const { data, error } = await supabase.functions.invoke<PayFastInitiateResponse>(
        'payfast-subscription-initiate',
        {
          body: { service_order_id: serviceOrderId, payment_token: token },
        }
      );

      if (error) {
        let code = 'unknown';
        try {
          const ctx = (error as { context?: { response?: Response } }).context;
          if (ctx?.response) {
            const body = await ctx.response.clone().json();
            if (typeof body?.code === 'string') code = body.code;
          }
        } catch { /* ignore */ }
        if (code === 'no_installment_plan') {
          setSubPhase('no_plan');
          return;
        }
        setSubError(friendlyErrorMessage(code));
        setSubPhase('error');
        return;
      }

      if (!data?.ok) {
        setSubError(friendlyErrorMessage('unknown'));
        setSubPhase('error');
        return;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.action_url;
      form.style.display = 'none';
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setSubError((err as Error).message || friendlyErrorMessage('unknown'));
      setSubPhase('error');
    }
  }

  return (
    <div className="text-center">
      <CheckCircle2 className="mx-auto h-10 w-10 text-success" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-navy-primary">
        Payment received — thank you.
      </h1>
      <p className="mt-2 text-sm text-foreground-soft">
        We're confirming the payment with PayFast right now. You'll receive
        a receipt by email within a minute. If your hosting account was
        suspended, it'll come back online automatically.
      </p>

      {/* Auto-pay migration flow — card saved, future cycles automatic */}
      {autopay && (
        <div className="mt-6 rounded border border-success/40 bg-success/5 p-3 text-sm text-foreground-soft">
          Automatic billing is now active — future months will be charged to
          your card by PayFast, no action needed. You can manage or cancel
          this any time in the portal under Billing → Auto-pay.
        </div>
      )}

      {/* Phase 10 — Studio Starter installment subscription handoff */}
      {!autopay && invoiceId && token && subPhase === 'idle' && (
        <div className="mt-6 rounded border border-warning/30 bg-warning/10 p-4 text-left">
          <p className="text-sm font-semibold text-warning-dark">
            Did you choose a payment plan?
          </p>
          <p className="mt-1 text-xs text-warning-dark">
            If you ordered Studio Starter with a 3 / 6 / 12-month plan, click below to register the monthly billing. (If you paid the full R3,990, you're already done.)
          </p>
          {/* These three used to be raw elements painting bg-primary with
              text-white — white on the brand orange is 2.26:1, on the one page where a
              customer is trying to give us money. Shared Button: correct
              contrast, focus ring and 44px target, all inherited. */}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSetupPlan}
            className="mt-3"
          >
            Set up monthly plan
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      {subPhase === 'starting' && (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-foreground-soft">
          <Loader2 className="h-4 w-4 animate-spin" />
          Setting up your payment plan…
        </div>
      )}
      {subPhase === 'no_plan' && (
        <div className="mt-6 rounded border border-success/40 bg-success/5 p-3 text-sm text-foreground-soft">
          You're all set — no recurring plan needed. Your order was paid in full.
        </div>
      )}
      {subPhase === 'error' && (
        <div className="mt-6 rounded border border-danger/40 bg-danger/5 p-3 text-left text-xs text-danger">
          {subError}
        </div>
      )}

      <Button asChild variant="primary" className="mt-6">
        <Link to="/sign-in">
          Sign in to the portal
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}

function CancelledState({
  invoiceId,
  token,
  autopay,
}: {
  invoiceId: string | undefined;
  token: string | null;
  autopay?: boolean;
}) {
  const retryHref =
    invoiceId && token
      ? `/pay/${invoiceId}?token=${token}${autopay ? '&autopay=1' : ''}`
      : '/sign-in';
  return (
    <div className="text-center">
      <AlertCircle className="mx-auto h-10 w-10 text-warning" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-navy-primary">
        Payment cancelled
      </h1>
      <p className="mt-2 text-sm text-foreground-soft">
        Your invoice is still unpaid. You can try again or pay by EFT — bank
        details are on the original invoice email.
      </p>
      <Button asChild variant="primary" className="mt-6">
        <a href={retryHref}>
          Try paying again
          <ArrowRight className="h-4 w-4" />
        </a>
      </Button>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center">
      <AlertCircle className="mx-auto h-10 w-10 text-danger" aria-hidden />
      <h1 className="mt-4 text-lg font-semibold text-navy-primary">
        Something went wrong
      </h1>
      <p className="mt-2 text-sm text-foreground-soft">{message}</p>
      <a
        href={`mailto:${siteConfig.supportEmail}`}
        className="mt-6 inline-flex items-center gap-2 rounded border border-input px-4 py-2 text-sm font-semibold text-navy-primary hover:bg-card-subtle"
      >
        <Mail className="h-4 w-4" />
        Email support
      </a>
    </div>
  );
}

function friendlyErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_token':
      return 'This payment link is invalid or has expired. Please check your invoice email or contact support.';
    case 'invoice_already_paid':
      return 'Good news — this invoice has already been paid.';
    case 'invoice_not_payable':
      return 'This invoice can no longer be paid online. Please email support for assistance.';
    case 'invoice_zero_amount':
      return 'There is no amount due on this invoice.';
    case 'already_subscribed':
      return 'This hosting account already has automatic billing set up — this invoice may already be covered. Sign in to the portal to check, or email support.';
    case 'not_a_hosting_invoice':
      return 'Automatic billing applies to hosting invoices only. Please use the regular payment link from your invoice email.';
    default:
      return "We couldn't start the payment right now. Please try again in a moment, or pay by EFT using the bank details in your invoice email.";
  }
}
